import { Navigate } from "react-router-dom";
import {
  CreditCard,
  Building2,
  Users,
  Receipt,
  AlertTriangle,
} from "lucide-react";
import { useAbility } from "@/hooks/use-ability";
import { useBilling, type BillingInfo } from "@/hooks/use-billing";
import { StatusBadge } from "@/components/status-badge";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const PLAN_LABELS: Record<BillingInfo["plan_type"], string> = {
  starter: "Starter",
  professional: "Professional",
  enterprise: "Enterprise",
};

function PlanCard({ billing }: { billing: BillingInfo }) {
  return (
    <div className="rounded-lg border bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-medium">Current Plan</h3>
        <StatusBadge status={billing.status} />
      </div>
      <div className="mb-4">
        <span className="inline-flex items-center rounded-full bg-secondary px-3 py-1 text-sm font-medium text-secondary-foreground">
          {PLAN_LABELS[billing.plan_type]}
        </span>
      </div>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <div>
          <dt className="text-muted-foreground">Started</dt>
          <dd className="font-medium">{formatDate(billing.plan_started_at)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">
            {billing.status === "trial" ? "Trial ends" : "Renews"}
          </dt>
          <dd className="font-medium">{formatDate(billing.plan_expires_at)}</dd>
        </div>
      </dl>
    </div>
  );
}

function UsageCard({ billing }: { billing: BillingInfo }) {
  return (
    <div className="rounded-lg border bg-card p-5">
      <h3 className="mb-4 text-sm font-medium">Usage</h3>
      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary">
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </div>
          <div>
            <p className="text-2xl font-semibold tabular-nums">
              {billing.business_count}
            </p>
            <p className="text-xs text-muted-foreground">Businesses</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary">
            <Users className="h-4 w-4 text-muted-foreground" />
          </div>
          <div>
            <p className="text-2xl font-semibold tabular-nums">
              {billing.user_count}
            </p>
            <p className="text-xs text-muted-foreground">Team members</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlaceholderSection({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-5">
      <h3 className="mb-4 text-sm font-medium">{title}</h3>
      <div className="flex flex-col items-center py-6 text-center">
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-secondary">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="max-w-sm text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

export default function SettingsBillingPage() {
  const ability = useAbility();
  const canAccess = ability.can("update", "Account");
  const {
    data: billing,
    isLoading,
    isError,
  } = useBilling({
    enabled: canAccess,
  });

  if (!canAccess) {
    return <Navigate to="/settings/api-keys" replace />;
  }

  if (isError) {
    return (
      <div className="rounded-lg border bg-card p-5">
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4" />
          Failed to load billing information.
        </div>
      </div>
    );
  }

  if (isLoading || !billing) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="h-36 animate-pulse rounded-lg border bg-card"
          />
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Billing</h2>
        <p className="text-sm text-muted-foreground">
          Manage your subscription and billing details.
        </p>
      </div>

      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <PlanCard billing={billing} />
          <UsageCard billing={billing} />
        </div>

        <PlaceholderSection
          icon={CreditCard}
          title="Payment Method"
          description="Payment methods will appear here once billing is configured."
        />

        <PlaceholderSection
          icon={Receipt}
          title="Billing History"
          description="Invoices and payment history will appear here once billing is configured."
        />

        <div className="rounded-lg border border-destructive/20 bg-card p-5">
          <h3 className="mb-1 text-sm font-medium">Cancel Subscription</h3>
          <p className="mb-4 text-sm text-muted-foreground">
            Cancellation will take effect at the end of your current billing
            period. Your data will be retained for 90 days.
          </p>
          <button
            disabled
            className="rounded-md border border-destructive/30 px-4 py-2 text-sm font-medium text-destructive opacity-50 cursor-not-allowed"
          >
            Cancel plan
          </button>
        </div>
      </div>
    </div>
  );
}
